// @ flow
import React from "react";
import {Button, Card, Checkbox, Tag, Tooltip} from "antd";
import {DeleteOutlined, InfoCircleOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";

import {pagePermissions, permissionKey} from "../../../../constants/pagePermissions";

// Tek bir sayfanın aksiyon yetkileri. `selectedKeys` yalnızca bu sayfanın seçili anahtarlarıdır.
// Görüntüleme (view) listenin başında normal bir checkbox olarak gösterilir. Görüntüleme
// dışında herhangi bir aksiyon seçiliyken görüntüleme otomatik seçilir — checkbox disabled
// değildir, kaldırılmaya çalışılırsa (diğer aksiyonlar hâlâ seçiliyken) handleChange onu
// tekrar ekler.
export const PagePermissionCard = ({page, selectedKeys, onChange, onRemove}) => {
    const {t} = useTranslation("pagePermissions");
    const viewKey = permissionKey(page, "view");
    const actions = ["view", ...pagePermissions[page].actions];
    const allKeys = actions.map(action => permissionKey(page, action));
    const isAllSelected = selectedKeys.length === allKeys.length;
    const handleChange = nextKeys => {
        const hasOtherActions = nextKeys.some(key => key !== viewKey);
        onChange(hasOtherActions ? [...new Set([...nextKeys, viewKey])] : nextKeys);
    };

    return (
        <Card
            size="small"
            style={{marginBottom: 8}}
            data-testid={`pagePermissionCard-${page}`}
            title={
                <>
                    {t(`pagePermissions.pages.${page}`)}{" "}
                    <Tag color="blue">
                        {t("pagePermissions.count", {
                            selected: selectedKeys.length,
                            total: allKeys.length
                        })}
                    </Tag>
                </>
            }
            extra={
                <>
                    <Button
                        type="link"
                        size="small"
                        data-testid={`pagePermissionToggleAll-${page}`}
                        onClick={() => handleChange(isAllSelected ? [] : allKeys)}
                    >
                        {t(
                            isAllSelected
                                ? "pagePermissions.deselectAll"
                                : "pagePermissions.selectAll"
                        )}
                    </Button>
                    <Button
                        type="text"
                        size="small"
                        title={t("pagePermissions.remove")}
                        data-testid={`pagePermissionRemove-${page}`}
                        icon={<DeleteOutlined />}
                        onClick={onRemove}
                    />
                </>
            }
        >
            <Checkbox.Group style={{width: "100%"}} value={selectedKeys} onChange={handleChange}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        rowGap: 20,
                        columnGap: 16
                    }}
                >
                    {actions.map(action => {
                        // Yalnızca birden fazla alt işlemi olan (gruplanmış) aksiyonlarda
                        // açıklama tanımlıdır; tekil aksiyonlarda etiket zaten yeterlidir.
                        const description = t(
                            `pagePermissions.actionDescriptions.${page}.${action}`,
                            {defaultValue: ""}
                        );
                        // Bazı ortak aksiyonlar (ör. "edit", "export") sayfaya göre farklı bir
                        // etiketle gösterilir; tanımlı değilse genel aksiyon etiketine düşer.
                        const label = t(`pagePermissions.actionLabels.${page}.${action}`, {
                            defaultValue: t(`pagePermissions.actions.${action}`)
                        });
                        return (
                            // Info ikonu her zaman sabit genişlikte ayrı bir sütunda durur; bu
                            // sayede etiket birden çok satıra taşsa bile ikon aşağı kaymaz,
                            // ikonu olan/olmayan satırlar aynı hizada kalır.
                            <div
                                key={action}
                                style={{display: "flex", alignItems: "flex-start", gap: 4}}
                            >
                                <Checkbox value={permissionKey(page, action)}>{label}</Checkbox>
                                <div style={{width: 16, flexShrink: 0}}>
                                    {description && (
                                        <Tooltip title={description}>
                                            <InfoCircleOutlined
                                                data-testid={`pagePermissionActionInfo-${page}-${action}`}
                                                style={{color: "#8c8c8c"}}
                                            />
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Checkbox.Group>
        </Card>
    );
};
